import React, { useState, useEffect } from 'react';
import Logo from '../../components/common/Logo';
import { ChevronDown, ChevronUp, Euro, FileText, Calendar, Info, X } from 'lucide-react';
import DoctorSidebar from '../../components/doctor/DoctorSidebar';
import AuthService from '../../services/auth.service';
import AppointmentService from '../../services/appoinment.service';
import { formatDate, formatTime } from '../../utils/dateUtils';


const DoctorBillingPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingData, setBillingData] = useState([]);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [commissionRate] = useState(AuthService.getCommissionRate());
  const [showModal, setShowModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      
      // Recupera gli appuntamenti del dottore
      const appointments = await AppointmentService.getDoctorAppointments();
      
      // Filtra solo appuntamenti confirmed
      const confirmedAppointments = appointments.filter(app => app.status === 'confirmed');
      
      // Genera i dati di fatturazione solo per i mesi con dati
      const billingStats = generateBillingData(confirmedAppointments);
      
      setBillingData(billingStats);
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento dei dati di fatturazione:', err);
      setError('Non è stato possibile caricare i dati di fatturazione. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const generateBillingData = (appointments) => {
    if (appointments.length === 0) return [];

    // Raggruppa gli appuntamenti per mese
    const appointmentsByMonth = {};
    
    appointments.forEach(appointment => {
      if (!appointment.date) return;
      
      const date = new Date(appointment.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleString('it-IT', { month: 'long', year: 'numeric' });
      
      if (!appointmentsByMonth[monthKey]) {
        appointmentsByMonth[monthKey] = {
          key: monthKey,
          name: monthName,
          appointments: [],
          count: 0,
          totalRevenue: 0,
          commission: 0,
          sortDate: date
        };
      }
      
      const price = parseFloat(appointment.price || 0);
      appointmentsByMonth[monthKey].appointments.push(appointment);
      appointmentsByMonth[monthKey].count += 1;
      appointmentsByMonth[monthKey].totalRevenue += price;
      appointmentsByMonth[monthKey].commission = appointmentsByMonth[monthKey].totalRevenue * (commissionRate / 100);
    });
    
    // Converti in array e ordina per data (più recente prima)
    return Object.values(appointmentsByMonth)
      .sort((a, b) => b.sortDate - a.sortDate);
  };

  const toggleMonthExpansion = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  const openMonthModal = (month) => {
    setSelectedMonth(month);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMonth(null);
  };

 

  const calculateTotals = () => {
    return billingData.reduce((totals, month) => ({
      totalAppointments: totals.totalAppointments + month.count,
      totalRevenue: totals.totalRevenue + month.totalRevenue,
      totalCommission: totals.totalCommission + month.commission
    }), { totalAppointments: 0, totalRevenue: 0, totalCommission: 0 });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <div className="w-64 bg-white shadow-md">
          <div className="p-4">
            <Logo />
          </div>
          <DoctorSidebar />
        </div>
        <div className="flex-1 bg-[#FAF4EB] p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-600">Caricamento dati di fatturazione...</p>
          </div>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <DoctorSidebar />
      </div>

      <div className="flex-1 bg-[#FAF4EB] p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-bold">Rendicontazione Mensile</h1>
          <p className="text-gray-600">Riepilogo appuntamenti e compensi per periodo</p>
        </header>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8">
            <p>{error}</p>
          </div>
        )}

        {/* Riepilogo Totali */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Appuntamenti Totali</p>
                <p className="text-2xl font-bold">{totals.totalAppointments}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Fatturato Totale</p>
                <p className="text-2xl font-bold">€{totals.totalRevenue.toFixed(2)}</p>
              </div>
              <Euro className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Compenso Totale ({commissionRate}%)</p>
                <p className="text-2xl font-bold">€{totals.totalCommission.toFixed(2)}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Tabella Mensile */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Dettaglio Mensile</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Periodo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    N° Appuntamenti
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Somma Tariffe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Compenso ({commissionRate}%)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {billingData.map((month) => (
                  <React.Fragment key={month.key}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 capitalize">
                          {month.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{month.count}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">€{month.totalRevenue.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-green-600">
                          €{month.commission.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-3">
                          <button
                            onClick={() => openMonthModal(month)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Visualizza dettagli appuntamenti"
                          >
                            <Info className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => toggleMonthExpansion(month.key)}
                            className="text-purple-600 hover:text-purple-900 flex items-center"
                            title="Espandi/Comprimi"
                          >
                            {expandedMonths[month.key] ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Riga espansa con riassunto */}
                    {expandedMonths[month.key] && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                              <p className="text-xs text-gray-500 uppercase">Appuntamenti</p>
                              <p className="text-lg font-semibold">{month.count}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500 uppercase">Fatturato</p>
                              <p className="text-lg font-semibold">€{month.totalRevenue.toFixed(2)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500 uppercase">Compenso</p>
                              <p className="text-lg font-semibold text-green-600">€{month.commission.toFixed(2)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500 uppercase">Tariffa Media</p>
                              <p className="text-lg font-semibold">€{(month.totalRevenue / month.count).toFixed(2)}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {billingData.length === 0 && !loading && (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun dato di fatturazione
            </h3>
            <p className="text-gray-500">
              Non ci sono appuntamenti confermati registrati.
            </p>
          </div>
        )}
      </div>

      {/* Modale dettagli mese */}
      {showModal && selectedMonth && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                Dettaglio Appuntamenti - {selectedMonth.name}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Riepilogo del mese nella modale */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Totale Appuntamenti</p>
                <p className="text-2xl font-bold">{selectedMonth.count}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Fatturato Mese</p>
                <p className="text-2xl font-bold">€{selectedMonth.totalRevenue.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Compenso ({commissionRate}%)</p>
                <p className="text-2xl font-bold text-green-600">€{selectedMonth.commission.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Tariffa Media</p>
                <p className="text-2xl font-bold">€{(selectedMonth.totalRevenue / selectedMonth.count).toFixed(2)}</p>
              </div>
            </div>

            {/* Tabella dettagliata appuntamenti */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orario</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paziente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servizio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tariffa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Compenso</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedMonth.appointments
                    .sort((a, b) => {
                      const dateA = new Date(a.date + 'T' + a.time);
                      const dateB = new Date(b.date + 'T' + b.time);
                      return dateA.getTime() - dateB.getTime();
                    })
                    .map((appointment) => {
                      const appointmentPrice = parseFloat(appointment.price || 0);
                      const appointmentCommission = appointmentPrice * (commissionRate / 100);
                      
                      return (
                        <tr key={appointment.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatDate(appointment.date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatTime(appointment.time)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {appointment.patient_name || `Paziente #${appointment.patient_id}`}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {appointment.service_name}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            €{appointmentPrice.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600">
                            €{appointmentCommission.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorBillingPage;